import React, { useState } from 'react';
import PageMeta from '../components/PageMeta';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, Star, Users, Award, Globe, TrendingUp, Target,
  Zap, BookOpen, Clock, Calendar, Phone, Mail, ChevronDown, Sparkles,
  Shield, Rocket, GraduationCap, Brain, Lightbulb, Loader2,
  Cpu, Box, Layers, Palette, Wand2, Shapes
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { trackWhatsAppClick, trackFormSubmission } from '../utils/analytics';
import CertificatesSection from '../components/CertificatesSection';
import AdmissionEnrollment from '../components/AdmissionEnrollment';
import EnquireNowButton from '../components/EnquireNowButton';
import HeroPricingCard from '../components/HeroPricingCard';
import BrandLogo from '../components/BrandLogo';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import MobileCTABar from '../components/MobileCTABar';

const BlenderCharacterCoursePage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/leads`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: formData.name, email: formData.email, phone: formData.phone, message: formData.message, course: 'Complete Blender & Character Design (4 Months)', source: 'blender-character-page' })
      });
      if (res.ok) { trackFormSubmission('blender_character_application', { course: 'Blender Character Design' }); navigate('/thank-you'); }
    } catch (err) { console.error(err); }
    setSubmitting(false);
  };

  const [payForm, setPayForm] = useState({ payName: '', payEmail: '', payPhone: '' });
  const [paySubmitting, setPaySubmitting] = useState(false);

  const handleAdmissionPayment = async (e) => {
    e.preventDefault();
    setPaySubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/initiate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstname: payForm.payName, email: payForm.payEmail, phone: payForm.payPhone, course_name: 'Blender & Character Design - Admission Fee', amount: 3000 })
      });
      if (res.ok) { const html = await res.text(); document.open(); document.write(html); document.close(); }
    } catch (err) { console.error(err); }
    setPaySubmitting(false);
  };

  const scrollToForm = () => document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' });

  const curriculum = [
    { month: 'Month 1', title: 'Blender Mastery', topics: ['Blender Interface & Navigation', 'Modeling Fundamentals & Topology', 'Lighting & Material Systems', 'UV Unwrapping & Texturing'] },
    { month: 'Month 2', title: 'Character Sculpting', topics: ['Digital Sculpting Techniques', 'Facial Design & Expressions', 'Clothing, Hair & Accessories', 'Character Asset Creation'] },
    { month: 'Month 3', title: 'Rigging & Animation', topics: ['Character Rigging Setup', 'Walk Cycles & Motion', 'Facial Animation & Lip Sync', 'Action Sequences & Expressions'] },
    { month: 'Month 4', title: 'Rendering & Portfolio', topics: ['Photorealistic Rendering (Cycles/EEVEE)', 'Portfolio Scene Composition', 'Client Project Simulation', 'Professional Showreel Creation'] }
  ];

  const careers = ['Character Designer', '3D Artist', 'Animator', 'Environment Designer', 'Motion Artist', 'NFT/Metaverse Designer', 'Freelance 3D Creator', 'Studio Artist'];
  const tools = ['Blender', 'ZBrush', 'Substance Painter', 'Marvelous Designer', 'Adobe Photoshop', 'After Effects', 'Unreal Engine', 'Mixamo'];

  const faqs = [
    { q: 'Is this course for absolute beginners?', a: 'Yes! We start from the very basics of Blender and build up to professional character design step by step.' },
    { q: 'Can I get freelance work after this?', a: 'Absolutely. Character design and 3D art are among the highest-paid freelance skills on platforms like Fiverr and Upwork.' },
    { q: 'Will I create a professional showreel?', a: 'Yes, Month 4 is entirely dedicated to building your portfolio and professional showreel for job applications.' },
    { q: 'What hardware do I need?', a: 'Any computer with 8GB+ RAM and a dedicated GPU. We provide guidance on optimal settings during the first class.' },
    { q: 'Are there internship opportunities?', a: 'Yes, top-performing students get internship opportunities at our partner studios and production houses.' }
  ];

  const testimonials = [
    { name: 'Rohan Das', role: '3D Artist at Animation Studio', quote: 'The character sculpting module was incredible. I went from zero Blender knowledge to getting hired at a studio in 5 months.' },
    { name: 'Meera Iyer', role: 'Freelance Character Designer', quote: 'I now earn $2000+/month on Fiverr creating 3D characters. This course was the best investment of my life.' },
    { name: 'Karan Singh', role: 'Motion Artist', quote: 'The rigging and animation training here is world-class. My showreel got me multiple job offers immediately.' }
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-x-hidden relative">
      <PageMeta
        title="Blender Character Design Course | Job-Assured | AI School for All"
        description="Complete Blender 3D character design. Industry projects, job assurance. Enroll at AI School for All."
        path="/complete-blender-character-design-course"
      />
      {/* WIREFRAME 3D MESH OVERLAY */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-[0.05]" style={{ backgroundImage: 'linear-gradient(rgba(139,92,246,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.6) 1px, transparent 1px)', backgroundSize: '60px 60px', transform: 'perspective(800px) rotateX(60deg) translateY(-20%)' }}></div>
      <div className="relative z-10">
      {/* STICKY MARQUEE */}
      <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 py-3">
        <div className="flex items-center justify-center gap-4 px-4">
          <div className="hidden md:flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-violet-200 animate-pulse" />
            <span className="text-sm font-bold text-white">Admissions Open for Admissions Open</span>
            <span className="text-slate-900/60">|</span>
            <span className="text-sm text-violet-200 font-semibold">Limited Seats — 60% OFF!</span>
          </div>
          <Button onClick={scrollToForm} className="bg-white text-violet-700 hover:bg-purple-100 px-6 py-2 text-sm font-bold rounded-full">
            Reserve Your Seat <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
      <SiteHeader />


      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-12 md:pt-16 overflow-hidden text-white">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1772371272179-3ecc656fc677?w=1920&h=1080&fit=crop&q=80" alt="3D Character Art" className="w-full h-full object-cover" decoding="async" fetchPriority="high" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/80 to-violet-900/40"></div>
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'linear-gradient(rgba(139,92,246,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.3) 1px, transparent 1px)', backgroundSize: '60px 60px' }}></div>
        </div>
        <div className="container mx-auto max-w-7xl px-6 relative z-10">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-violet-500/20 border border-violet-500/30 rounded-full px-4 py-2 mb-6">
              <Shapes className="w-4 h-4 text-violet-400" />
              <span className="text-sm font-bold text-violet-300">4-Month Industry Program</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight">
              Master Blender, Character Design &<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-300">3D Animation</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-2xl">Learn professional 3D modelling, character sculpting, rigging, animation, and rendering — build a showreel that lands you jobs worldwide.</p>
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button onClick={scrollToForm} className="bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white px-8 py-6 text-lg font-bold rounded-xl">
                Enroll Now (60% OFF) <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <EnquireNowButton courseName="Blender & Character Design" size="md" label="Enquire Now" variant="outline" />
            </div>
            <div className="flex flex-wrap gap-4">
              {['Offline + Online Hybrid', 'Industry Ready Program', 'Limited Seats'].map(t => (
                <div key={t} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2">
                  <CheckCircle2 className="w-4 h-4 text-violet-400" /><span className="text-sm text-gray-300">{t}</span>
                </div>
              ))}
            </div>
            </div>
            <div className="lg:col-span-5">
              <HeroPricingCard
                courseName="Blender & Character Design"
                oldPrice={75000}
                newPrice={30000}
                discountLabel="60% OFF"
                badge="4-Month Industry Program"
                batchDate="New Batches Every Month"
                theme="violet"
                whatsappMessage="Hi, I want to know about the Blender Character Design Course"
              />
            </div>
          </div>
        </div>
      </section>

      {/* PREMIUM TRAINER PROFILE - 3rd Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-black via-violet-950/40 to-purple-950/30 relative overflow-hidden">
        <div className="absolute top-1/4 left-0 w-[500px] h-[500px] rounded-full bg-violet-500/15 blur-[140px]"></div>
        <div className="absolute bottom-1/4 right-0 w-[400px] h-[400px] rounded-full bg-pink-500/15 blur-[120px]"></div>
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'linear-gradient(rgba(139,92,246,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.5) 1px, transparent 1px)', backgroundSize: '60px 60px' }}></div>

        <div className="container mx-auto max-w-7xl relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-violet-500/20 border border-violet-500/40 rounded-full px-5 py-2 mb-5">
              <Award className="w-4 h-4 text-violet-400" />
              <span className="text-xs font-bold text-violet-300 uppercase tracking-widest">Meet Your Industry Mentor</span>
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4 leading-tight">
              Learn From a <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-purple-300 to-pink-300">Real 3D Character Artist</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">Master Blender &amp; character design with a working animation professional.</p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-2 relative">
              <div className="relative group">
                <div className="absolute -inset-2 bg-gradient-to-r from-violet-500 via-purple-400 to-pink-500 rounded-3xl blur-xl opacity-40 group-hover:opacity-70 transition-opacity"></div>
                <div className="relative rounded-3xl overflow-hidden ring-4 ring-violet-500/40 shadow-2xl shadow-violet-500/30">
                  <img
                    src="https://customer-assets.emergentagent.com/job_8d33ea52-c764-4e32-b8e2-de0e8040a6fa/artifacts/jyd37jw6_WhatsApp%20Image%202026-05-09%20at%2010.54.31.jpeg"
                    alt="Mr. Our Expert Trainers — Lead Trainer"
                    className="w-full aspect-[4/5] object-cover"
                    data-testid="trainer-saurabh-photo" decoding="async" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <p className="text-xs font-bold text-violet-300 uppercase tracking-widest mb-1">Lead Trainer</p>
                    <p className="text-3xl font-black text-white">Mr. Our Expert Trainers</p>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 bg-gradient-to-br from-violet-500 to-purple-600 text-white rounded-2xl px-4 py-3 shadow-2xl rotate-3 hidden md:block">
                  <p className="text-xs font-bold opacity-90">EXPERIENCE</p>
                  <p className="text-2xl font-black">15+ Yrs</p>
                </div>
                <div className="absolute -bottom-4 -left-4 bg-white text-black rounded-2xl px-4 py-3 shadow-2xl -rotate-3 hidden md:block">
                  <p className="text-xs font-bold opacity-70">STUDENTS</p>
                  <p className="text-2xl font-black">5,000+</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-3 space-y-6">
              <div>
                <h3 className="text-5xl md:text-6xl font-black text-white mb-3 leading-none">Mr. Our Expert Trainers</h3>
                <p className="text-xl text-violet-300 font-semibold">Lead Trainer — Blender, Character Design &amp; 3D Animation</p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {[
                  { num: '15+', label: 'Years Experience' },
                  { num: '5,000+', label: 'Students Trained' },
                  { num: '100+', label: 'Characters Designed' }
                ].map((s, i) => (
                  <div key={i} className="bg-gradient-to-br from-violet-500/10 to-purple-500/5 border border-violet-500/30 rounded-2xl p-4 text-center backdrop-blur-sm">
                    <p className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-br from-violet-300 to-pink-400">{s.num}</p>
                    <p className="text-xs text-gray-400 mt-1">{s.label}</p>
                  </div>
                ))}
              </div>

              <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                A Blender expert and character artist with over <span className="font-bold text-violet-300">15 years</span> in the animation industry. Expert Trainer has trained thousands of students in character modelling, sculpting, rigging, and animation — many of whom now work at studios producing for OTT, gaming, and ad agencies.
              </p>

              <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3 pt-2">
                {[
                  'Blender mastery from beginner to advanced',
                  'Character modelling, sculpting &amp; rigging',
                  '3D animation &amp; rendering workflows',
                  'Animation industry production experience',
                  'Hands-on, project-based teaching',
                  'Personal mentorship through every project'
                ].map((t, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-violet-400 flex-shrink-0 mt-0.5" />
                    <span dangerouslySetInnerHTML={{ __html: t }} />
                  </div>
                ))}
              </div>

              <div className="border-l-4 border-violet-500 pl-5 py-2 italic text-gray-300 bg-violet-500/5 rounded-r-xl">
                "Characters tell stories. I teach you how to bring them to life — one polygon at a time."
                <p className="not-italic text-violet-400 font-bold text-sm mt-2">— Mr. Our Expert Trainers</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY BLENDER */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-4">Why Blender Skills are in <span className="text-violet-600">Global Demand</span></h2>
          <p className="text-slate-600 text-center mb-16 max-w-2xl mx-auto">3D art and character design are the foundation of the modern creative economy.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { icon: <Layers className="w-6 h-6" />, title: 'Animation Studios' },
              { icon: <Cpu className="w-6 h-6" />, title: 'Gaming Industry' },
              { icon: <Globe className="w-6 h-6" />, title: 'Metaverse' },
              { icon: <Brain className="w-6 h-6" />, title: 'AI Content' },
              { icon: <TrendingUp className="w-6 h-6" />, title: 'Freelancing' }
            ].map((item, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-center hover:border-violet-400 transition-all">
                <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center text-violet-600 mx-auto mb-3">{item.icon}</div>
                <p className="text-sm font-semibold text-slate-900">{item.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAREERS */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-16">Career <span className="text-violet-600">Opportunities</span></h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="rounded-2xl overflow-hidden border border-slate-200">
              <img src="https://images.unsplash.com/photo-1743247299142-35028faf885d?w=600&h=350&fit=crop&q=80" alt="3D Character Art" className="w-full h-48 object-cover" decoding="async" loading="lazy" />
              <div className="p-6 bg-slate-50">
                <h3 className="text-lg font-bold text-slate-900 mb-3">Character & 3D Art</h3>
                <div className="flex flex-wrap gap-2">
                  {['Character Designer', '3D Artist', 'Animator', 'Environment Designer'].map((r, i) => (
                    <span key={i} className="bg-violet-100 text-violet-600 text-xs font-semibold px-3 py-1.5 rounded-full border border-violet-200">{r}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-slate-200">
              <img src="https://images.unsplash.com/photo-1743247299142-76ba40201c5d?w=600&h=350&fit=crop&q=80" alt="Animation & Motion" className="w-full h-48 object-cover" decoding="async" loading="lazy" />
              <div className="p-6 bg-slate-50">
                <h3 className="text-lg font-bold text-slate-900 mb-3">Motion & Emerging</h3>
                <div className="flex flex-wrap gap-2">
                  {['Motion Artist', 'NFT/Metaverse Designer', 'Freelance 3D Creator', 'Studio Artist'].map((r, i) => (
                    <span key={i} className="bg-violet-100 text-violet-600 text-xs font-semibold px-3 py-1.5 rounded-full border border-violet-200">{r}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-12 text-center">
            <p className="text-slate-600">Starting salary: <span className="text-violet-600 font-bold text-2xl">&#8377;4-12 LPA</span> | International freelancers earn <span className="text-violet-600 font-bold text-2xl">$2K-8K/month</span></p>
          </div>
        </div>
      </section>

      {/* CURRICULUM */}
      <section id="curriculum" className="py-24 px-6 bg-white relative">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 70% 50%, rgba(139,92,246,0.4) 0%, transparent 40%)' }}></div>
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-4">4-Month <span className="text-violet-600">Curriculum</span></h2>
          <p className="text-slate-600 text-center mb-16">From basics to professional character designer</p>
          <div className="grid md:grid-cols-2 gap-6">
            {curriculum.map((m, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 hover:border-violet-400 transition-all">
                <span className="text-xs font-bold uppercase tracking-wider text-violet-600 bg-violet-100 px-3 py-1 rounded-full">{m.month}</span>
                <h3 className="text-lg font-bold text-slate-900 mt-4 mb-4">{m.title}</h3>
                <ul className="space-y-2">{m.topics.map((t, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-violet-600 flex-shrink-0 mt-0.5" />{t}</li>
                ))}</ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STUDENT PORTFOLIO */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-16">Student <span className="text-violet-600">Portfolio</span></h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'Character Design Portfolio', img: 'https://images.unsplash.com/photo-1743247299142-35028faf885d?w=600&h=400&fit=crop&q=80', desc: 'Stylized character sculpts — from concept art to fully textured 3D models.' },
              { title: 'Animation Showreel', img: 'https://images.unsplash.com/photo-1743247299142-76ba40201c5d?w=600&h=400&fit=crop&q=80', desc: 'Walk cycles, facial expressions, combat sequences — professional-grade animation reels.' },
              { title: 'Environment & Scene Art', img: 'https://images.unsplash.com/photo-1743247299142-8f1c919776c4?w=600&h=400&fit=crop&q=80', desc: 'Fantasy worlds, sci-fi environments, and cinematic scene compositions.' }
            ].map((p, i) => (
              <div key={i} className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-2xl overflow-hidden hover:border-violet-400/40 transition-all group">
                <img src={p.img} alt={p.title} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" decoding="async" loading="lazy" />
                <div className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{p.title}</h3>
                  <p className="text-sm text-slate-600">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STUDENT WORK SHOWCASE — Horizontal Scrolling Gallery */}
      <section className="py-24 px-0 bg-gradient-to-br from-black via-violet-950/30 to-purple-950/30 relative overflow-hidden">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[80%] h-[2px] bg-gradient-to-r from-transparent via-violet-500/40 to-transparent"></div>
        <div className="absolute top-1/2 right-0 w-[400px] h-[400px] bg-violet-500/10 rounded-full blur-[140px]"></div>
        <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-pink-500/10 rounded-full blur-[140px]"></div>

        <div className="container mx-auto max-w-7xl px-6 relative mb-12">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="inline-flex items-center gap-2 bg-violet-500/20 border border-violet-500/40 rounded-full px-4 py-1.5 mb-4">
                <Sparkles className="w-4 h-4 text-violet-400" />
                <span className="text-xs font-bold text-violet-300 uppercase tracking-widest">Characters Crafted By Our Students</span>
              </div>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight">
                Student <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-purple-300 to-pink-300">Showreel</span>
              </h2>
              <p className="text-lg text-gray-400 mt-3 max-w-xl">3D characters, sculpts &amp; animations produced by our Blender students.</p>
            </div>
            <div className="text-sm text-violet-300 font-semibold flex items-center gap-2">
              <span className="hidden md:inline">Scroll horizontally</span>
              <ArrowRight className="w-4 h-4 animate-pulse" />
            </div>
          </div>
        </div>

        <div data-testid="student-showcase-scroll" className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-8 px-6 md:px-12 scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
          {[
            { title: 'Stylized Hero Character', tag: 'BLENDER SCULPT', img: 'https://images.unsplash.com/photo-1622979135225-d2ba269cf1ac?w=700&h=500&fit=crop&q=80', desc: 'Pixar-style hero characters with full sculpt detail & topology.' },
            { title: 'Realistic Portrait', tag: 'PHOTOREAL', img: 'https://images.unsplash.com/photo-1633613286848-e6f43bbafb8d?w=700&h=500&fit=crop&q=80', desc: 'Photoreal portrait renders with skin shaders & cinematic lighting.' },
            { title: 'Character Rig & Animation', tag: 'RIGGING', img: 'https://images.unsplash.com/photo-1559128010-7c1ad6e1b6a5?w=700&h=500&fit=crop&q=80', desc: 'Full character rigging with walk-cycles & action animations.' },
            { title: 'Stylized Environment', tag: '3D ENVIRONMENT', img: 'https://images.unsplash.com/photo-1620421680010-0766ff230392?w=700&h=500&fit=crop&q=80', desc: 'Game-ready environments with props, lighting & texturing.' },
            { title: 'Creature Concept Sculpt', tag: 'CONCEPT ART', img: 'https://images.unsplash.com/photo-1500099817043-86d46000d58f?w=700&h=500&fit=crop&q=80', desc: 'Creature concept sculpts ready for film & game production.' },
            { title: 'Animation Showreel', tag: 'ANIMATION', img: 'https://images.unsplash.com/photo-1543966888-7c1dc482a810?w=700&h=500&fit=crop&q=80', desc: '30-second animation showreel — what every student walks away with.' },
            { title: 'Stylized Product Render', tag: 'PRODUCT', img: 'https://images.unsplash.com/photo-1502134249126-9f3755a50d78?w=700&h=500&fit=crop&q=80', desc: 'Branded product visualization & 3D ad-ready renders.' }
          ].map((p, i) => (
            <div key={i} className="snap-start flex-shrink-0 w-[300px] md:w-[380px] group cursor-pointer">
              <div className="relative rounded-2xl overflow-hidden border border-violet-500/20 hover:border-violet-400/60 transition-all">
                <img src={p.img} alt={p.title} className="w-full h-[260px] md:h-[300px] object-cover group-hover:scale-110 transition-transform duration-700" decoding="async" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"></div>
                <div className="absolute top-3 left-3">
                  <span className="bg-violet-500/90 text-white text-[10px] font-black px-2.5 py-1 rounded-md tracking-widest backdrop-blur-sm">{p.tag}</span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="text-xl font-black text-white mb-2">{p.title}</h3>
                  <p className="text-sm text-gray-300 line-clamp-2">{p.desc}</p>
                </div>
              </div>
            </div>
          ))}
          <div className="flex-shrink-0 w-6"></div>
        </div>

        <div className="text-center mt-6 text-sm text-gray-500">
          <span className="inline-flex items-center gap-2"><Sparkles className="w-3 h-3 text-violet-400" /> Swipe / drag to explore <Sparkles className="w-3 h-3 text-violet-400" /></span>
        </div>
      </section>

      <CertificatesSection theme="violet" courseName="Mastering Blender & Character Design" />

      {/* WHY AI SCHOOL */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-16">Why <span className="text-violet-600">AI School For All</span></h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: <Award />, title: "20+ Year Legacy", desc: 'Workshop Library\'s decades of training excellence.' },
              { icon: <Globe />, title: 'Global Ecosystem', desc: 'Students trained across India and internationally.' },
              { icon: <Target />, title: 'Industry Curriculum', desc: 'Designed with studios and production professionals.' },
              { icon: <Rocket />, title: 'Real Projects', desc: 'Work on production-grade character and scene designs.' },
              { icon: <Users />, title: 'Mentorship', desc: 'Personal guidance from Mr. Our Expert Trainers and team.' },
              { icon: <Palette />, title: 'Showreel Ready', desc: 'Graduate with a professional animation showreel.' }
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 bg-slate-50 border border-slate-200 rounded-xl p-5 hover:border-violet-400 transition-all">
                <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center text-violet-600 flex-shrink-0">{item.icon}</div>
                <div><h3 className="font-bold text-slate-900 mb-1">{item.title}</h3><p className="text-sm text-slate-600">{item.desc}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-16">What Students <span className="text-violet-600">Say</span></h2>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
                <div className="flex gap-1 mb-4">{[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-violet-400 text-violet-600" />)}</div>
                <p className="text-slate-700 mb-4 italic">"{t.quote}"</p>
                <div><p className="font-bold text-slate-900">{t.name}</p><p className="text-sm text-violet-600">{t.role}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CERT + TOOLS */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-black mb-6">Industry <span className="text-violet-600">Certification</span></h2>
              <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-2xl p-8">
                <Award className="w-12 h-12 text-violet-600 mb-4" />
                <h3 className="text-xl font-bold text-slate-900 mb-2">AI School For All Certificate</h3>
                <p className="text-slate-600 mb-4">Powered by Workshop Library — recognized by animation studios and tech companies.</p>
                <ul className="space-y-2">
                  {['Industry-recognized credential', 'Professional showreel included', 'Placement assistance provided', 'Internship at partner studios'].map((t, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-violet-600" />{t}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-black mb-6">Tools You'll <span className="text-violet-600">Master</span></h2>
              <div className="grid grid-cols-2 gap-3">
                {tools.map((t, i) => (
                  <div key={i} className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center hover:border-violet-400 transition-all">
                    <p className="text-sm font-semibold text-slate-900">{t}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-16">Frequently Asked <span className="text-violet-600">Questions</span></h2>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                  <span className="font-semibold text-slate-900 pr-4">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-violet-600 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-5 text-slate-600 text-sm">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* DETAILED ENROLLMENT + ADMISSION PAYMENT */}
      <AdmissionEnrollment courseName="Blender & Character Design" theme="violet" source="violet-course-page" />
      <MobileCTABar courseName="Blender Character Design" />

<SiteFooter accent="violet" />

      {/* WHATSAPP */}
      <a href="https://wa.me/919891550666?text=Hi, I want to know about the Blender Character Design Course" target="_blank" rel="noopener noreferrer"
        onClick={() => trackWhatsAppClick('blender_page')} className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl transition-all hover:scale-110" data-testid="whatsapp-float">
        <Phone className="w-6 h-6" />
      </a>

      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white/95 backdrop-blur-md border-t border-violet-200 shadow-2xl p-3">
        <Button onClick={scrollToForm} className="w-full bg-gradient-to-r from-violet-500 to-purple-500 text-white py-3 font-bold rounded-xl">
          Enroll Now — &#8377;30,000 (60% OFF)
        </Button>
      </div>
      </div>
    </div>
  );
};

export default BlenderCharacterCoursePage;
